/**
 * Advanced Analytics Dashboard
 * Provides LFL analysis, GMROI, Supplier Scorecards, Lost Sales, and Sell-Through Rate tabs.
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  CircularProgress,
  TextField,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from 'recharts';
import api from '../../services/api';
import TabPanel from '../Common/TabPanel';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import { CHART } from '../../constants/chartColors';

const COLORS = [CHART.primary, CHART.success, CHART.taupe, '#FF8042', '#8B2438', '#C9A96E', '#16A34A', '#DC2626', '#8dd1e1'];

const currentYear = new Date().getFullYear();

const pctColor = (value: number): string => (value >= 0 ? '#4caf50' : '#f44336');

// ===========================================================================
// TAB 1 — Like-for-Like Analysis
// ===========================================================================
const LFLTab: React.FC = () => {
  const { t } = useTranslation();
  const { fmtCurrencyPrecise, fmtCurrencyLabel } = useCurrencyFormat();
  const [periodType, setPeriodType] = useState<string>('monthly');
  const [year, setYear] = useState<number>(currentYear);

  const { data, isLoading, error } = useQuery({
    queryKey: ['lflAnalysis', periodType, year],
    queryFn: async () => {
      const response = await api.getLFLAnalysis({ period_type: periodType, year });
      return response.data;
    },
  });

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{t('advancedAnalytics.errorLoading')}</Alert>;

  const lflGrowth: number = data?.lfl_growth_pct ?? 0;
  const comparisonData: any[] = data?.comparison ?? [];
  const storeData: any[] = data?.stores ?? [];

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('advancedAnalytics.periodType')}</InputLabel>
          <Select value={periodType} label={t('advancedAnalytics.periodType')} onChange={(e) => setPeriodType(e.target.value)}>
            <MenuItem value="monthly">{t('advancedAnalytics.monthly')}</MenuItem>
            <MenuItem value="quarterly">{t('advancedAnalytics.quarterly')}</MenuItem>
            <MenuItem value="yearly">{t('advancedAnalytics.yearly')}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('advancedAnalytics.year')}</InputLabel>
          <Select value={year} label={t('advancedAnalytics.year')} onChange={(e) => setYear(Number(e.target.value))}>
            {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="outlined" startIcon={<ExportIcon />}>{t('advancedAnalytics.export')}</Button>
      </Box>

      {/* KPI Card */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary">{t('advancedAnalytics.lflGrowth')}</Typography>
              <Typography variant="h3" sx={{ color: pctColor(lflGrowth), fontWeight: 700 }}>
                {lflGrowth >= 0 ? '+' : ''}{lflGrowth.toFixed(2)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bar Chart — current vs prior year */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>{t('advancedAnalytics.revenueComparison')}</Typography>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={comparisonData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="period" />
            <YAxis tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(value: number) => fmtCurrencyPrecise(value)} />
            <Legend />
            <Bar dataKey="current_revenue" name={t('advancedAnalytics.currentYear')} fill={CHART.primary} />
            <Bar dataKey="prior_revenue" name={t('advancedAnalytics.priorYear')} fill={CHART.taupe} />
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      {/* Store Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('advancedAnalytics.store')}</TableCell>
              <TableCell align="right">{t('advancedAnalytics.currentRevenue')} {fmtCurrencyLabel()}</TableCell>
              <TableCell align="right">{t('advancedAnalytics.priorRevenue')} {fmtCurrencyLabel()}</TableCell>
              <TableCell align="right">{t('advancedAnalytics.growth')} %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {storeData.map((row: any, idx: number) => {
              const growth: number = row.growth_pct ?? 0;
              return (
                <TableRow key={idx}>
                  <TableCell>{row.store_name ?? row.store_id}</TableCell>
                  <TableCell align="right">{fmtCurrencyPrecise(row.current_revenue)}</TableCell>
                  <TableCell align="right">{fmtCurrencyPrecise(row.prior_revenue)}</TableCell>
                  <TableCell align="right" sx={{ color: pctColor(growth), fontWeight: 600 }}>
                    {growth >= 0 ? '+' : ''}{growth.toFixed(2)}%
                  </TableCell>
                </TableRow>
              );
            })}
            {storeData.length === 0 && (
              <TableRow><TableCell colSpan={4} align="center">{t('advancedAnalytics.noData')}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ===========================================================================
// TAB 2 — GMROI
// ===========================================================================
const GMROITab: React.FC = () => {
  const { t } = useTranslation();
  const { fmtCurrencyPrecise, fmtCurrencyLabel } = useCurrencyFormat();
  const [category, setCategory] = useState<string>('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['gmroi', category],
    queryFn: async () => {
      const params: any = {};
      if (category) params.category = category;
      const response = await api.getGMROI(params);
      return response.data;
    },
  });

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{t('advancedAnalytics.errorLoading')}</Alert>;

  const products: any[] = data?.products ?? [];
  const categories: string[] = data?.categories ?? [];

  // Top 10 for horizontal bar chart
  const top10 = [...products].sort((a, b) => (b.gmroi ?? 0) - (a.gmroi ?? 0)).slice(0, 10);

  const gmroiChipColor = (val: number): 'success' | 'warning' | 'error' => {
    if (val > 3) return 'success';
    if (val >= 1) return 'warning';
    return 'error';
  };

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>{t('advancedAnalytics.category')}</InputLabel>
          <Select value={category} label={t('advancedAnalytics.category')} onChange={(e) => setCategory(e.target.value)}>
            <MenuItem value="">{t('advancedAnalytics.allCategories')}</MenuItem>
            {categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="outlined" startIcon={<ExportIcon />}>{t('advancedAnalytics.export')}</Button>
      </Box>

      {/* Horizontal Bar Chart — Top 10 GMROI */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>{t('advancedAnalytics.top10GMROI')}</Typography>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={top10} layout="vertical" margin={{ left: 120 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis type="category" dataKey="product_name" width={110} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(value: number) => value.toFixed(2)} />
            <Legend />
            <Bar dataKey="gmroi" name="GMROI" fill={CHART.success} />
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      {/* Product Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('advancedAnalytics.product')}</TableCell>
              <TableCell>{t('advancedAnalytics.sku')}</TableCell>
              <TableCell>{t('advancedAnalytics.category')}</TableCell>
              <TableCell align="right">{t('advancedAnalytics.grossMargin')} {fmtCurrencyLabel()}</TableCell>
              <TableCell align="right">{t('advancedAnalytics.avgInventoryCost')} {fmtCurrencyLabel()}</TableCell>
              <TableCell align="right">GMROI</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((row: any, idx: number) => (
              <TableRow key={idx}>
                <TableCell>{row.product_name}</TableCell>
                <TableCell>{row.sku}</TableCell>
                <TableCell>{row.category}</TableCell>
                <TableCell align="right">{fmtCurrencyPrecise(row.gross_margin)}</TableCell>
                <TableCell align="right">{fmtCurrencyPrecise(row.avg_inventory_cost)}</TableCell>
                <TableCell align="right">
                  <Chip
                    label={(row.gmroi ?? 0).toFixed(2)}
                    size="small"
                    color={gmroiChipColor(row.gmroi ?? 0)}
                  />
                </TableCell>
              </TableRow>
            ))}
            {products.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center">{t('advancedAnalytics.noData')}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ===========================================================================
// TAB 3 — Supplier Scorecards
// ===========================================================================
const SupplierScorecardsTab: React.FC = () => {
  const { t } = useTranslation();
  const [supplierId, setSupplierId] = useState<string>('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['supplierScorecards', supplierId],
    queryFn: async () => {
      const params: any = {};
      if (supplierId) params.supplier_id = supplierId;
      const response = await api.getSupplierScorecards(params);
      return response.data;
    },
  });

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{t('advancedAnalytics.errorLoading')}</Alert>;

  const suppliers: any[] = data?.suppliers ?? [];
  const supplierOptions: any[] = data?.supplier_options ?? suppliers;

  const gradeColor = (grade: string): 'success' | 'info' | 'warning' | 'error' => {
    switch (grade?.toUpperCase()) {
      case 'A': return 'success';
      case 'B': return 'info';
      case 'C': return 'warning';
      case 'D': return 'error';
      default: return 'info';
    }
  };

  // Radar chart: top 5 suppliers
  const radarSuppliers = suppliers.slice(0, 5);
  const radarMetrics = [
    { metric: t('advancedAnalytics.deliveryCompliance'), key: 'delivery_compliance' },
    { metric: t('advancedAnalytics.fillRate'), key: 'fill_rate' },
    { metric: t('advancedAnalytics.avgLeadTime'), key: 'avg_lead_time_score' },
    { metric: t('advancedAnalytics.qualityScore'), key: 'quality_score' },
  ];

  const radarData = radarMetrics.map((m) => {
    const entry: any = { metric: m.metric };
    radarSuppliers.forEach((s: any) => {
      entry[s.supplier_name] = s[m.key] ?? 0;
    });
    return entry;
  });

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t('advancedAnalytics.supplier')}</InputLabel>
          <Select value={supplierId} label={t('advancedAnalytics.supplier')} onChange={(e) => setSupplierId(e.target.value)}>
            <MenuItem value="">{t('advancedAnalytics.allSuppliers')}</MenuItem>
            {supplierOptions.map((s: any) => (
              <MenuItem key={s.supplier_id ?? s.id} value={s.supplier_id ?? s.id}>{s.supplier_name ?? s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="outlined" startIcon={<ExportIcon />}>{t('advancedAnalytics.export')}</Button>
      </Box>

      {/* Supplier Cards Grid */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {suppliers.map((s: any, idx: number) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>{s.supplier_name}</Typography>
                  <Chip label={s.grade ?? 'N/A'} size="small" color={gradeColor(s.grade)} />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t('advancedAnalytics.deliveryCompliance')}: <strong>{(s.delivery_compliance ?? 0).toFixed(1)}%</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('advancedAnalytics.fillRate')}: <strong>{(s.fill_rate ?? 0).toFixed(1)}%</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('advancedAnalytics.avgLeadTime')}: <strong>{(s.avg_lead_time ?? 0).toFixed(1)} {t('advancedAnalytics.days')}</strong>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {suppliers.length === 0 && (
          <Grid item xs={12}>
            <Alert severity="info">{t('advancedAnalytics.noData')}</Alert>
          </Grid>
        )}
      </Grid>

      {/* Radar Chart */}
      {radarSuppliers.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>{t('advancedAnalytics.supplierComparison')}</Typography>
          <ResponsiveContainer width="100%" height={400}>
            <RadarChart data={radarData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="metric" tick={{ fontSize: 12 }} />
              <PolarRadiusAxis angle={30} domain={[0, 100]} />
              {radarSuppliers.map((s: any, idx: number) => (
                <Radar
                  key={idx}
                  name={s.supplier_name}
                  dataKey={s.supplier_name}
                  stroke={COLORS[idx % COLORS.length]}
                  fill={COLORS[idx % COLORS.length]}
                  fillOpacity={0.15}
                />
              ))}
              <Legend />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </Paper>
      )}
    </Box>
  );
};

// ===========================================================================
// TAB 4 — Lost Sales
// ===========================================================================
const LostSalesTab: React.FC = () => {
  const { t } = useTranslation();
  const { fmtCurrencyPrecise, fmtCurrencyLabel } = useCurrencyFormat();
  const defaultFrom = new Date();
  defaultFrom.setMonth(defaultFrom.getMonth() - 1);
  const [dateFrom, setDateFrom] = useState<string>(defaultFrom.toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState<string>(new Date().toISOString().split('T')[0]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['lostSales', dateFrom, dateTo],
    queryFn: async () => {
      const response = await api.getLostSales({ date_from: dateFrom, date_to: dateTo });
      return response.data;
    },
  });

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{t('advancedAnalytics.errorLoading')}</Alert>;

  const totalRevenue: number = data?.total_lost_revenue ?? 0;
  const totalUnits: number = data?.total_lost_units ?? 0;
  const incidents: number = data?.stockout_incidents ?? 0;
  const byCategory: any[] = data?.by_category ?? [];
  const details: any[] = data?.details ?? [];

  return (
    <Box>
      {/* Date Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          type="date"
          size="small"
          label={t('advancedAnalytics.dateFrom')}
          InputLabelProps={{ shrink: true }}
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
        />
        <TextField
          type="date"
          size="small"
          label={t('advancedAnalytics.dateTo')}
          InputLabelProps={{ shrink: true }}
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
        />
        <Button variant="outlined" startIcon={<ExportIcon />}>{t('advancedAnalytics.export')}</Button>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary">{t('advancedAnalytics.totalLostRevenue')}</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#DC2626' }}>{fmtCurrencyPrecise(totalRevenue)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary">{t('advancedAnalytics.totalLostUnits')}</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{totalUnits.toLocaleString()}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary">{t('advancedAnalytics.stockoutIncidents')}</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{incidents.toLocaleString()}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pie Chart — Lost sales by category */}
      {byCategory.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>{t('advancedAnalytics.lostSalesByCategory')}</Typography>
          <ResponsiveContainer width="100%" height={350}>
            <PieChart>
              <Pie
                data={byCategory}
                dataKey="lost_revenue"
                nameKey="category"
                cx="50%"
                cy="50%"
                outerRadius={120}
                label={({ name, percent }: any) => `${name} (${(percent * 100).toFixed(1)}%)`}
              >
                {byCategory.map((_entry: any, idx: number) => (
                  <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value: number) => fmtCurrencyPrecise(value)} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Detail Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('advancedAnalytics.product')}</TableCell>
              <TableCell>{t('advancedAnalytics.sku')}</TableCell>
              <TableCell>{t('advancedAnalytics.store')}</TableCell>
              <TableCell align="right">{t('advancedAnalytics.duration')} ({t('advancedAnalytics.days')})</TableCell>
              <TableCell align="right">{t('advancedAnalytics.lostUnits')}</TableCell>
              <TableCell align="right">{t('advancedAnalytics.lostRevenue')} {fmtCurrencyLabel()}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {details.map((row: any, idx: number) => (
              <TableRow key={idx}>
                <TableCell>{row.product_name}</TableCell>
                <TableCell>{row.sku}</TableCell>
                <TableCell>{row.store_name ?? row.store_id}</TableCell>
                <TableCell align="right">{row.duration_days ?? 0}</TableCell>
                <TableCell align="right">{(row.lost_units ?? 0).toLocaleString()}</TableCell>
                <TableCell align="right">{fmtCurrencyPrecise(row.lost_revenue)}</TableCell>
              </TableRow>
            ))}
            {details.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center">{t('advancedAnalytics.noData')}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ===========================================================================
// TAB 5 — Sell-Through Rate
// ===========================================================================
const SellThroughTab: React.FC = () => {
  const { t } = useTranslation();
  const [periodMonths, setPeriodMonths] = useState<number>(6);

  const { data, isLoading, error } = useQuery({
    queryKey: ['sellThrough', periodMonths],
    queryFn: async () => {
      const response = await api.getSellThroughRate({ period_months: periodMonths });
      return response.data;
    },
  });

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{t('advancedAnalytics.errorLoading')}</Alert>;

  const products: any[] = data?.products ?? [];

  const stColor = (pct: number): 'success' | 'warning' | 'error' => {
    if (pct > 80) return 'success';
    if (pct >= 50) return 'warning';
    return 'error';
  };

  // Build distribution histogram data
  const ranges = [
    { label: '0-20%', min: 0, max: 20 },
    { label: '20-40%', min: 20, max: 40 },
    { label: '40-60%', min: 40, max: 60 },
    { label: '60-80%', min: 60, max: 80 },
    { label: '80-100%', min: 80, max: 100 },
    { label: '>100%', min: 100, max: Infinity },
  ];

  const histogramData = ranges.map((r) => ({
    range: r.label,
    count: products.filter((p: any) => {
      const st = p.sell_through_pct ?? 0;
      return st >= r.min && st < r.max;
    }).length,
  }));

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('advancedAnalytics.period')}</InputLabel>
          <Select value={periodMonths} label={t('advancedAnalytics.period')} onChange={(e) => setPeriodMonths(Number(e.target.value))}>
            <MenuItem value={3}>3 {t('advancedAnalytics.months')}</MenuItem>
            <MenuItem value={6}>6 {t('advancedAnalytics.months')}</MenuItem>
            <MenuItem value={12}>12 {t('advancedAnalytics.months')}</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" startIcon={<ExportIcon />}>{t('advancedAnalytics.export')}</Button>
      </Box>

      {/* Histogram Chart */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>{t('advancedAnalytics.sellThroughDistribution')}</Typography>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={histogramData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="range" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Bar dataKey="count" name={t('advancedAnalytics.productCount')} fill="#8B2438" />
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('advancedAnalytics.product')}</TableCell>
              <TableCell>{t('advancedAnalytics.sku')}</TableCell>
              <TableCell>{t('advancedAnalytics.category')}</TableCell>
              <TableCell align="right">{t('advancedAnalytics.beginningStock')}</TableCell>
              <TableCell align="right">{t('advancedAnalytics.received')}</TableCell>
              <TableCell align="right">{t('advancedAnalytics.sold')}</TableCell>
              <TableCell align="right">{t('advancedAnalytics.sellThroughPct')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((row: any, idx: number) => {
              const st: number = row.sell_through_pct ?? 0;
              return (
                <TableRow key={idx}>
                  <TableCell>{row.product_name}</TableCell>
                  <TableCell>{row.sku}</TableCell>
                  <TableCell>{row.category}</TableCell>
                  <TableCell align="right">{(row.beginning_stock ?? 0).toLocaleString()}</TableCell>
                  <TableCell align="right">{(row.received ?? 0).toLocaleString()}</TableCell>
                  <TableCell align="right">{(row.sold ?? 0).toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <Chip label={`${st.toFixed(1)}%`} size="small" color={stColor(st)} />
                  </TableCell>
                </TableRow>
              );
            })}
            {products.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center">{t('advancedAnalytics.noData')}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ===========================================================================
// Main Dashboard Component
// ===========================================================================
const AdvancedAnalyticsDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <AssessmentIcon sx={{ fontSize: 36, color: 'primary.main' }} />
        <Typography variant="h4">{t('advancedAnalytics.title')}</Typography>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
      >
        <Tab label={t('advancedAnalytics.lfl')} />
        <Tab label={t('advancedAnalytics.gmroi')} />
        <Tab label={t('advancedAnalytics.supplierScorecards')} />
        <Tab label={t('advancedAnalytics.lostSales')} />
        <Tab label={t('advancedAnalytics.sellThrough')} />
      </Tabs>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <LFLTab />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <GMROITab />
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <SupplierScorecardsTab />
      </TabPanel>
      <TabPanel value={tabValue} index={3}>
        <LostSalesTab />
      </TabPanel>
      <TabPanel value={tabValue} index={4}>
        <SellThroughTab />
      </TabPanel>
    </Box>
  );
};

export default AdvancedAnalyticsDashboard;
